/**
 * Barcode Parser Utility
 *
 * Handles formatting of multi-field barcode search strings
 * for work order operation filtering.
 *
 * Format: "workOrderId | sequenceNumber: 10 | lot: ABC | sub: SUB1 | split: SP1"
 */

import {
  WORK_ORDER_OPERATION_FIELDS,
  parseSearchFields,
} from 'lib/utils/workOrderOperationSearch';

// Field display names for formatting
const FIELD_DISPLAY_NAMES = {
  [WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]: 'workOrderId',
  [WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER]: 'sequenceNumber',
  [WORK_ORDER_OPERATION_FIELDS.LOT]: 'lot',
  [WORK_ORDER_OPERATION_FIELDS.SUB]: 'sub',
  [WORK_ORDER_OPERATION_FIELDS.SPLIT]: 'split',
};

const DELIMITER = ' | ';
const FIELD_SEPARATOR = ': ';

/**
 * Format field values into a search string
 *
 * @param {Object} fields - Object with field names as keys
 * @param {Array<string>} fieldOrder - Optional array of field names in desired order
 * @returns {string} Formatted search string
 *
 * @example
 * formatSearchString({ workOrderId: "12345", sequenceNumber: "10" })
 * // Returns: "workOrderId: 12345 | sequenceNumber: 10"
 */
export const formatSearchString = (fields, fieldOrder = null) => {
  if (!fields || typeof fields !== 'object') {
    return '';
  }

  // Use provided field order or default order
  const orderedFields =
    fieldOrder ||
    Object.keys(WORK_ORDER_OPERATION_FIELDS).map((key) => {
      return WORK_ORDER_OPERATION_FIELDS[key];
    });

  // Add fields in order with field name prefix
  const parts = orderedFields
    .filter((fieldName) => {
      return fields[fieldName];
    })
    .map((fieldName) => {
      const displayName = FIELD_DISPLAY_NAMES[fieldName] || fieldName;
      return `${displayName}${FIELD_SEPARATOR}${fields[fieldName]}`;
    });

  return parts.join(DELIMITER);
};

/**
 * Append a new field to an existing search string
 *
 * @param {string} existingSearch - Current search string
 * @param {string} fieldName - Name of the field to append
 * @param {string} value - Value for the field
 * @param {Array<string>} fieldOrder - Optional array of field names in desired order
 * @returns {string} Updated search string
 *
 * @example
 * appendField("workOrderId: 12345", "sequenceNumber", "10")
 * // Returns: "workOrderId: 12345 | sequenceNumber: 10"
 */
export const appendField = (
  existingSearch,
  fieldName,
  value,
  fieldOrder = null
) => {
  if (!fieldName || !value) {
    return existingSearch || '';
  }

  const currentFields = parseSearchFields(existingSearch);

  currentFields[fieldName] = value;

  return formatSearchString(currentFields, fieldOrder);
};

/**
 * Determine the next expected field based on scan order
 *
 * @param {Object} currentFields - Currently parsed fields
 * @param {Array<string>} fieldOrder - Optional array of field names in desired order
 * @returns {string|null} Next expected field name or null if all filled
 *
 * @example
 * getNextExpectedField({ workOrderId: "123" }, ['workOrderId', 'sequenceNumber'])
 * // Returns: 'sequenceNumber'
 */
export const getNextExpectedField = (currentFields, fieldOrder = null) => {
  // Use provided field order or default order
  const orderedFields = fieldOrder || [
    WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID,
    WORK_ORDER_OPERATION_FIELDS.SEQUENCE_NUMBER,
    WORK_ORDER_OPERATION_FIELDS.LOT,
    WORK_ORDER_OPERATION_FIELDS.SUB,
    WORK_ORDER_OPERATION_FIELDS.SPLIT,
  ];

  const nextField = orderedFields.find((field) => {
    return !currentFields[field];
  });
  return nextField || null; // All fields filled if no next field found
};

/**
 * Intelligently append a scanned value to search string
 * Determines which field to append based on what's already present
 *
 * @param {string} existingSearch - Current search string
 * @param {string} scannedValue - Newly scanned value
 * @param {Array<string>} fieldOrder - Optional array of field names in desired order
 * @returns {string} Updated search string
 *
 * @example
 * smartAppend("workOrderId: 12345", "10")
 * // Returns: "workOrderId: 12345 | sequenceNumber: 10"
 */
export const smartAppend = (
  existingSearch,
  scannedValue,
  fieldOrder = null
) => {
  if (!scannedValue) {
    return existingSearch || '';
  }

  const currentFields = parseSearchFields(existingSearch);
  const nextField = getNextExpectedField(currentFields, fieldOrder);

  if (!nextField) {
    // All fields already filled, start over with first field
    const firstField = fieldOrder
      ? fieldOrder[0]
      : WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID;
    return formatSearchString({ [firstField]: scannedValue }, fieldOrder);
  }

  return appendField(existingSearch, nextField, scannedValue);
};

/**
 * Check if a search string contains multiple fields
 *
 * @param {string} searchString - Search string to check
 * @returns {boolean} True if contains multiple fields
 */
export const hasMultipleFields = (searchString) => {
  if (!searchString) return false;
  return searchString.includes(DELIMITER);
};

/**
 * Replace a specific field value in an existing search string
 *
 * @param {string} existingSearch - Current search string
 * @param {string} fieldName - Name of the field to replace
 * @param {string} newValue - New value for the field
 * @param {Array<string>} fieldOrder - Optional array of field names in desired order
 * @returns {string} Updated search string
 *
 * @example
 * replaceField("workOrderId: 12345 | sequenceNumber: 10", "workOrderId", "67890")
 * // Returns: "workOrderId: 67890 | sequenceNumber: 10"
 */
export const replaceField = (
  existingSearch,
  fieldName,
  newValue,
  fieldOrder = null
) => {
  if (!fieldName || !newValue) {
    return existingSearch || '';
  }

  const currentFields = parseSearchFields(existingSearch);

  // Replace the specified field with new value
  currentFields[fieldName] = newValue;

  return formatSearchString(currentFields, fieldOrder);
};

/**
 * Remove a specific field from an existing search string
 *
 * @param {string} existingSearch - Current search string
 * @param {string} fieldName - Name of the field to remove
 * @param {Array<string>} fieldOrder - Optional array of field names in desired order
 * @returns {string} Updated search string
 *
 * @example
 * removeField("workOrderId: 12345 | sequenceNumber: 10", "sequenceNumber")
 * // Returns: "workOrderId: 12345"
 */
export const removeField = (existingSearch, fieldName, fieldOrder = null) => {
  if (!fieldName) {
    return existingSearch || '';
  }

  const currentFields = parseSearchFields(existingSearch);

  // Remove the specified field
  delete currentFields[fieldName];

  return formatSearchString(currentFields, fieldOrder);
};

/**
 * Get a human-readable description of parsed fields
 *
 * @param {string} searchString - Search string to describe
 * @returns {string} Description of fields
 */
export const getFieldsDescription = (searchString) => {
  const fields = parseSearchFields(searchString);
  const fieldCount = Object.keys(fields).length;

  if (fieldCount === 0) return 'No filters';
  if (fieldCount === 1 && fields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]) {
    return `Filtering by Work Order: ${
      fields[WORK_ORDER_OPERATION_FIELDS.WORK_ORDER_ID]
    }`;
  }

  return `Filtering by ${fieldCount} fields`;
};
